import { useEffect, useRef, useState } from "react";
import { useFetcher } from "react-router";
import type { Route } from "./+types/partner-application";
import { MarketingLayout } from "~/components/marketing/MarketingLayout";
import { GoldMesh } from "~/components/marketing/svg/GoldMesh";
import { detectLocale, translations } from "~/i18n/index";
import { useT } from "~/i18n/context";
import { socialMeta } from "~/lib/seo";

export async function loader({ request }: Route.LoaderArgs) {
  const locale = detectLocale(
    request.headers.get("Cookie"),
    request.headers.get("Accept-Language"),
  );
  return { locale };
}

export function meta({ data }: Route.MetaArgs) {
  const locale = data?.locale ?? "en";
  const t = translations[locale].partnerApplication.meta;
  return [
    { title: t.title },
    { name: "description", content: t.description },
    ...socialMeta({ title: t.title, description: t.description, pathname: "/partner-application", locale }),
  ];
}

function useReveal(threshold = 0.08) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add("visible"); },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return ref;
}

export default function PartnerApplicationPage() {
  const { t } = useT();
  const f = t.forManufacturers.inquiryForm;
  const fetcher = useFetcher<{ ok: boolean; error?: string }>();
  const heroRef = useReveal(0.15);
  const formRef = useReveal();

  const [form, setForm] = useState({
    companyName: "",
    contactName: "",
    email: "",
    phone: "",
    website: "",
    location: "",
    capabilities: [] as string[],
    specializations: [] as string[],
    certifications: [] as string[],
    productionCapacity: "",
    additionalInfo: "",
  });

  function toggleMulti(field: "capabilities" | "specializations" | "certifications", value: string) {
    setForm((prev) => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter((v) => v !== value)
        : [...prev[field], value],
    }));
  }

  const isSubmitting = fetcher.state !== "idle";
  const isSuccess = fetcher.data?.ok === true && !fetcher.data?.error;
  const errorKey = fetcher.data?.ok === false ? fetcher.data.error : null;
  const errorMessage =
    errorKey === "required_fields"
      ? f.errorRequired
      : errorKey === "invalid_email"
        ? f.errorEmail
        : errorKey === "send_failed"
          ? f.errorSend
          : null;

  return (
    <MarketingLayout>
      {/* ══ HERO ══ */}
      <section className="px-6 lg:px-12 pt-16 pb-14 lg:pt-24 lg:pb-20 relative overflow-hidden">
        <GoldMesh opacity={0.06} />
        <div ref={heroRef} className="reveal relative">
          <p className="issue-label mb-6" style={{ fontFamily: "var(--font-sans)" }}>{f.overline}</p>
          <h1
            className="font-display font-light leading-[0.92] tracking-tight text-foreground max-w-3xl"
            style={{ fontSize: "clamp(2.8rem, 6vw, 5.5rem)" }}
          >
            {f.heading.split("\n").map((line, i) => (
              <span key={i}>
                {i === 0 ? line : <em className="not-italic" style={{ fontWeight: 300, opacity: 0.45 }}>{line}</em>}
                {i === 0 && <br />}
              </span>
            ))}
          </h1>
          <p
            className="mt-8 text-muted-foreground leading-relaxed"
            style={{ fontSize: "clamp(0.92rem, 1.2vw, 1.05rem)", maxWidth: "56ch" }}
          >
            {f.subtitle}
          </p>
        </div>
      </section>

      <div className="px-6 lg:px-12"><div className="rule" /></div>

      {/* ══ APPLICATION FORM ══ */}
      <section className="px-6 lg:px-12 py-16 lg:py-24">
        <div ref={formRef} className="reveal">
          {isSuccess ? (
            <div className="max-w-2xl">
              <h2 className="font-display font-light text-foreground leading-tight mb-4" style={{ fontSize: "clamp(2rem, 4vw, 3.2rem)" }}>
                {f.successHeading}
              </h2>
              <p className="text-muted-foreground leading-relaxed" style={{ fontSize: "0.92rem", maxWidth: "52ch" }}>
                {f.successBody}
              </p>
              <a
                href="/for-manufacturers"
                className="inline-block mt-10 px-8 py-4 bg-teal text-teal-fg hover:bg-teal-hover transition-colors"
                style={{ fontSize: "0.68rem", letterSpacing: "0.2em", textTransform: "uppercase" }}
              >
                {t.forManufacturers.hero.cta}
              </a>
            </div>
          ) : (
            <>
              {errorMessage && (
                <p className="text-sm text-destructive mb-6">{errorMessage}</p>
              )}

              <fetcher.Form method="post" action="/resources/manufacturer-inquiry" className="max-w-2xl space-y-8">
                {/* Honeypot */}
                <input type="text" name="website_url" className="sr-only" tabIndex={-1} autoComplete="off" aria-hidden="true" />

                {/* ── Contact info ── */}
                <div className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label={`${f.companyName} *`} name="companyName" value={form.companyName} onChange={(v) => setForm((p) => ({ ...p, companyName: v }))} />
                    <Field label={`${f.contactName} *`} name="contactName" value={form.contactName} onChange={(v) => setForm((p) => ({ ...p, contactName: v }))} />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label={`${f.email} *`} name="email" type="email" value={form.email} onChange={(v) => setForm((p) => ({ ...p, email: v }))} />
                    <Field label={f.phone} name="phone" type="tel" value={form.phone} onChange={(v) => setForm((p) => ({ ...p, phone: v }))} />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label={f.website} name="website" type="url" value={form.website} onChange={(v) => setForm((p) => ({ ...p, website: v }))} placeholder="https://" />
                    <Field label={`${f.location} *`} name="location" value={form.location} onChange={(v) => setForm((p) => ({ ...p, location: v }))} />
                  </div>
                </div>

                <div className="rule" />

                {/* ── Capabilities ── */}
                <CheckboxGroup
                  label={f.capabilities}
                  hint={f.capabilitiesHint}
                  options={f.capabilityOptions}
                  selected={form.capabilities}
                  onToggle={(v) => toggleMulti("capabilities", v)}
                />
                {form.capabilities.map((v) => <input key={v} type="hidden" name="capabilities" value={v} />)}

                {/* ── Specializations ── */}
                <CheckboxGroup
                  label={f.specializations}
                  hint={f.specializationsHint}
                  options={f.specializationOptions}
                  selected={form.specializations}
                  onToggle={(v) => toggleMulti("specializations", v)}
                />
                {form.specializations.map((v) => <input key={v} type="hidden" name="specializations" value={v} />)}

                {/* ── Certifications ── */}
                <CheckboxGroup
                  label={f.certifications}
                  hint={f.certificationsHint}
                  options={f.certificationOptions}
                  selected={form.certifications}
                  onToggle={(v) => toggleMulti("certifications", v)}
                />
                {form.certifications.map((v) => <input key={v} type="hidden" name="certifications" value={v} />)}

                <div className="rule" />

                {/* ── Production capacity ── */}
                <RadioGroup
                  label={`${f.productionCapacity} *`}
                  name="productionCapacity"
                  options={f.capacityOptions}
                  selected={form.productionCapacity}
                  onSelect={(v) => setForm((p) => ({ ...p, productionCapacity: v }))}
                />

                {/* ── Additional info ── */}
                <div>
                  <label htmlFor="additionalInfo" className="block text-[10px] tracking-[0.14em] uppercase text-muted-foreground mb-2">
                    {f.additionalInfo}
                  </label>
                  <textarea
                    id="additionalInfo"
                    name="additionalInfo"
                    rows={4}
                    value={form.additionalInfo}
                    onChange={(e) => setForm((p) => ({ ...p, additionalInfo: e.target.value }))}
                    placeholder={f.additionalInfoPlaceholder}
                    className="w-full px-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:border-foreground/40 transition-colors text-foreground placeholder:text-muted-foreground/60 resize-y"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-8 py-4 bg-teal text-teal-fg hover:bg-teal-hover transition-colors disabled:opacity-50"
                  style={{ fontSize: "0.68rem", letterSpacing: "0.2em", textTransform: "uppercase" }}
                >
                  {isSubmitting ? f.submitting : f.submit}
                </button>
              </fetcher.Form>
            </>
          )}
        </div>
      </section>
    </MarketingLayout>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function Field({
  label, name, type = "text", value, onChange, placeholder,
}: {
  label: string; name: string; type?: string;
  value: string; onChange: (v: string) => void; placeholder?: string;
}) {
  return (
    <div>
      <label htmlFor={name} className="block text-[10px] tracking-[0.14em] uppercase text-muted-foreground mb-2">
        {label}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-10 px-3 text-sm bg-background border border-border rounded-lg focus:outline-none focus:border-foreground/40 transition-colors text-foreground placeholder:text-muted-foreground/60"
      />
    </div>
  );
}

function CheckboxGroup({
  label, hint, options, selected, onToggle,
}: {
  label: string; hint?: string; options: string[];
  selected: string[]; onToggle: (v: string) => void;
}) {
  return (
    <div>
      <p className="text-[10px] tracking-[0.14em] uppercase text-muted-foreground mb-1">{label}</p>
      {hint && <p className="text-xs text-muted-foreground/60 mb-3">{hint}</p>}
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const active = selected.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onToggle(opt)}
              className="h-8 px-3 rounded-full text-xs font-medium transition-all"
              style={{
                background: active ? "var(--teal-sapphire)" : "var(--muted)",
                color: active ? "var(--teal-fg)" : "var(--muted-foreground)",
                border: active ? "1px solid transparent" : "1px solid var(--border)",
              }}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function RadioGroup({
  label, name, options, selected, onSelect,
}: {
  label: string; name: string; options: string[];
  selected: string; onSelect: (v: string) => void;
}) {
  return (
    <div>
      <p className="text-[10px] tracking-[0.14em] uppercase text-muted-foreground mb-3">{label}</p>
      <div className="flex flex-col gap-2">
        {options.map((opt) => (
          <label key={opt} className="flex items-center gap-3 cursor-pointer group">
            <div
              className="h-4 w-4 rounded-full border flex-shrink-0 flex items-center justify-center transition-all"
              style={{
                borderColor: selected === opt ? "var(--teal-sapphire)" : "var(--border)",
                background: selected === opt ? "var(--teal-sapphire)" : "transparent",
              }}
            >
              {selected === opt && <div className="h-1.5 w-1.5 rounded-full bg-white" />}
            </div>
            <input
              type="radio"
              className="sr-only"
              name={name}
              value={opt}
              checked={selected === opt}
              onChange={() => onSelect(opt)}
            />
            <span className="text-sm text-foreground group-hover:text-foreground/80 transition-colors">{opt}</span>
          </label>
        ))}
      </div>
    </div>
  );
}
